import React, { useState, useEffect } from 'react';
import { formatDate } from './dateFormat';
import { useNavigate } from 'react-router-dom';
import { url, authFetch, userMoveInDepositUrl } from './apiClient';
import DepositHistoryModal from './DepositHistoryModal';

const ROLES = ['ADMIN', 'TENANT'];
// How a deposit paid outside the app actually arrived (backend: CASH | UPI | BANK_TRANSFER).
const DEPOSIT_METHODS = [['CASH', 'Cash'], ['UPI', 'UPI'], ['BANK_TRANSFER', 'Bank transfer']];

// Admins first, then tenants in natural order: Room1, Room2, ... Room10 (not Room1, Room10, Room2).
const sortUsers = (list) => [...list].sort((a, b) =>
  (a.role === 'ADMIN' ? 0 : 1) - (b.role === 'ADMIN' ? 0 : 1) ||
  a.username.localeCompare(b.username, undefined, { numeric: true, sensitivity: 'base' })
);

const styles = {
  container: {
    maxWidth: '100%',
    margin: '0 auto',
    padding: '32px',
    background: 'var(--surface)',
    borderRadius: '16px',
    boxShadow: 'var(--shadow)',
  },
  header: {
    color: 'var(--primary)',
    textAlign: 'center',
    marginBottom: '24px',
    letterSpacing: '1px',
  },
  card: {
    marginBottom: '32px',
    padding: '20px',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  input: {
    marginRight: '10px',
    padding: '8px',
    border: '1px solid var(--line-strong)',
    borderRadius: '6px',
    fontSize: '15px',
  },
  btnPrimary: {
    background: 'var(--grad-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 18px',
    fontWeight: 'bold',
    fontSize: '15px',
    cursor: 'pointer',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    minWidth: '1080px',
    borderCollapse: 'collapse',
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  th: {
    padding: '12px',
    background: 'var(--grad-primary)',
    color: '#fff',
  },
  td: {
    padding: '10px',
    textAlign: 'center',
  },
  actionBtn: {
    borderRadius: '4px',
    padding: '6px 14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginRight: '6px',
  },
  actionsCol: {
    width: '180px',
    minWidth: '180px',
    maxWidth: '180px',
  },
  usernameCol: {
    width: '180px',
    minWidth: '180px',
    maxWidth: '180px',
  },
  searchBar: {
    marginBottom: '18px',
    padding: '8px',
    border: '1px solid var(--line-strong)',
    borderRadius: '6px',
    fontSize: '15px',
    width: '220px',
  },
  pagination: {
    marginTop: '18px',
    textAlign: 'center',
  },
  exportBtn: {
    marginLeft: '12px',
    padding: '7px 16px',
    borderRadius: '6px',
    background: 'var(--primary-light)',
    color: '#fff',
    border: 'none',
    fontWeight: 'bold',
    cursor: 'pointer',
  }
};

function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const emptyNewUser = { username: '', role: 'TENANT', moveInDate: '', demandedDeposit: '', depositPaid: '', depositMethod: 'CASH' };
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [editId, setEditId] = useState(null);
  const [depositHistoryFor, setDepositHistoryFor] = useState(null); // username whose deposit history popup is open
  const [editUser, setEditUser] = useState({ username: '', role: ROLES[0], fullName: '', phone: '', moveInDate: '', demandedDeposit: '', manualDepositAmount: '', manualDepositNotes: '', manualDepositMethod: 'CASH', manualDepositReference: '', manualDepositReceivedOn: '' });
  const [updatingMoveIn, setUpdatingMoveIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [regFilter, setRegFilter] = useState('ALL');

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const registeredCount = users.filter(u => u.registrationCompleted).length;
  const filteredUsers = users.filter(
    u =>
      (regFilter === 'ALL' || (regFilter === 'REGISTERED') === !!u.registrationCompleted) &&
      (u.username.toLowerCase().includes(search.toLowerCase()) ||
        u.role.toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const pagedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  // Auth guard: must be ADMIN
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) return navigate('/login', { replace: true });
    const { role } = JSON.parse(stored);
    if (role !== 'ADMIN') navigate('/', { replace: true });
  }, [navigate]);

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
  const res = await authFetch(url.adminUsersAll());
      if (!res.ok) throw new Error('Failed to fetch users');
  const data = await res.json();
  setUsers(sortUsers(data));
    } catch (err) {
      console.error(err);
      setError('Unable to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handlers
  const handleAddChange = e => {
    setNewUser(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddUser = async () => {
    if (!newUser.username.trim()) {
      return alert('Enter a username, for example Room1');
    }
    setLoading(true);
    try {
      // No password here: the tenant chooses their own when they register.
      const res = await authFetch(url.adminUserAdd(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUser.username.trim(), role: newUser.role })
      });
      if (!res.ok) {
        // Try to extract backend error message (JSON or text) so duplicate user errors show up.
        let backendMsg = '';
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const data = await res.json();
            backendMsg = data.message || data.error || JSON.stringify(data);
          } else {
            backendMsg = (await res.text()) || res.statusText;
          }
        } catch (_) {
          backendMsg = res.statusText || 'Unknown error';
        }
        throw new Error(backendMsg ? `Add failed: ${backendMsg}` : 'Add failed');
      }
      const created = await res.json().catch(() => ({}));
      if (created.registrationKey) {
        alert(`User "${created.username}" added.\n\nRegistration key: ${created.registrationKey}\n\nGive this to the tenant -- they need it to register. It won't be shown again here, but you can generate a new one with "New Key" if it's lost.`);
      }
      const hasDetails = newUser.role === 'TENANT' && (newUser.moveInDate || newUser.demandedDeposit !== '' || newUser.depositPaid !== '');
      if (created.id && hasDetails) {
        const detailsRes = await authFetch(userMoveInDepositUrl.update(created.id), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(newUser.moveInDate ? { moveInDate: newUser.moveInDate } : {}),
            ...(newUser.demandedDeposit !== '' ? { demandedDeposit: newUser.demandedDeposit } : {}),
            ...(newUser.depositPaid !== '' ? { manualDepositAmount: newUser.depositPaid, method: newUser.depositMethod, notes: 'Deposit paid before joining the app' } : {})
          })
        });
        if (!detailsRes.ok) {
          const detailsErr = await detailsRes.json().catch(() => ({}));
          alert(`User added, but the move-in/deposit details were not saved: ${detailsErr.error || 'unknown error'}. Add them with Edit.`);
        }
      }
      setNewUser(emptyNewUser);
      await fetchUsers();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error adding user');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = user => {
    setEditId(user.id);
    setEditUser({
      username: user.username,
      role: user.role,
      fullName: user.fullName || '',
      phone: user.phone || '',
      registrationCompleted: !!user.registrationCompleted,
      moveInDate: user.moveInDate || '',
      demandedDeposit: user.demandedDeposit != null ? user.demandedDeposit : '',
      // Deliberately blank, not pre-filled with the current total -- this
      // field adds a new deposit ledger entry, it doesn't overwrite the total.
      manualDepositAmount: '',
      manualDepositNotes: '',
      manualDepositMethod: 'CASH',
      manualDepositReference: '',
      manualDepositReceivedOn: ''
    });
  };

  const handleEditChange = e => {
    const { name, value } = e.target;
    setEditUser(prev => ({ ...prev, [name]: name === 'registrationCompleted' ? value === 'true' : value }));
  };

  const handleUpdateUser = async () => {
    if (!editUser.username || !editUser.role) {
      return alert('Username and Role are required to update');
    }
    setLoading(true);
    try {
      // Exclude password from update (prevents sending hashed value back & double hashing backend)
      const { username, role, phone, fullName } = editUser;
      // Empty phone / name are sent as empty strings so they can be cleared.
      const basePayload = {
        username, role, phone: phone.trim(), fullName: fullName.trim(),
        registrationCompleted: role === 'ADMIN' ? true : editUser.registrationCompleted,
      };
      const res = await authFetch(url.adminUserUpdate(editId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(basePayload)
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Update failed');
      }
      const updated = await res.json().catch(() => ({}));
      if (updated.registrationKey && updated.registrationCompleted === false) {
        alert(`"${updated.username}" is now Not Registered.\n\nNew registration key: ${updated.registrationKey}\n\nGive this to the new tenant -- the old key no longer works.`);
      }
      // After basic update, optionally update move-in date and/or record a
      // manual deposit entry if provided
      if (editUser.role !== 'ADMIN' && (editUser.moveInDate || editUser.demandedDeposit !== '' || editUser.manualDepositAmount)) {
        try {
          setUpdatingMoveIn(true);
          const depositRes = await authFetch(userMoveInDepositUrl.update(editId), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...(editUser.moveInDate ? { moveInDate: editUser.moveInDate } : {}),
              ...(editUser.demandedDeposit !== '' ? { demandedDeposit: editUser.demandedDeposit } : {}),
              ...(editUser.manualDepositAmount ? {
                manualDepositAmount: editUser.manualDepositAmount,
                method: editUser.manualDepositMethod,
                reference: editUser.manualDepositReference || undefined,
                receivedOn: editUser.manualDepositReceivedOn || undefined,
                notes: editUser.manualDepositNotes || undefined
              } : {})
            })
          });
          if (!depositRes.ok) {
            const data = await depositRes.json().catch(() => ({}));
            throw new Error(data.error || 'Move-in/deposit update failed');
          }
        } catch (e) {
          console.warn('Move-in/deposit patch failed', e);
          alert(e.message || 'Move-in date or deposit update failed');
        }
        finally { setUpdatingMoveIn(false); }
      }
      setEditId(null);
      await fetchUsers();
    } catch (err) {
      console.error(err);
      alert(err.message && err.message !== 'Update failed' ? err.message : 'Error updating user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    setLoading(true);
    try {
  const res = await authFetch(url.adminUserDelete(id), { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Error deleting user');
    } finally {
      setLoading(false);
    }
  };

  // Export users as CSV -- quoted so a name or note containing a comma doesn't
  // split into the wrong column when opened in Excel/Sheets.
  const csvCell = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;

  const handleExportCSV = () => {
    const csvRows = [
      // No registration keys here -- "Export Registration Keys" is the separate, deliberate way to get those.
      ['ID', 'Username', 'Full Name', 'Role', 'Email', 'Phone', 'Registration Status', 'Move-in Date', 'Demanded Deposit', 'Deposit Paid'],
      ...users.map(u => [
        u.id, u.username, u.fullName || '', u.role, u.mail || '', u.phone || '',
        u.registrationCompleted ? 'Registered' : 'Not registered',
        formatDate(u.moveInDate),
        u.demandedDeposit ?? '',
        u.role === 'TENANT' ? (u.totalAmountDeposited ?? 0) : '',
      ]),
    ];
    const csvContent = csvRows.map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = link;
    a.download = 'users.csv';
    a.click();
    URL.revokeObjectURL(link);
  };

  // Just the pending tenants and their keys -- the one thing an owner needs
  // to hand out, without the rest of the user table getting in the way.
  const handleExportKeysCSV = () => {
    const pending = users.filter(u => u.role === 'TENANT' && !u.registrationCompleted && u.registrationKey);
    const csvRows = [
      ['Username', 'Registration Key'],
      ...pending.map(u => [u.username, u.registrationKey]),
    ];
    const csvContent = csvRows.map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = link;
    a.download = 'registration-keys.csv';
    a.click();
    URL.revokeObjectURL(link);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>User Management</h2>
      {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

      <div style={styles.card}>
        <h4 style={{ color: 'var(--primary)', marginBottom: '6px' }}>Add New User</h4>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>
          Enter only the username (for example Room1). The tenant sets their own password, name and contact details when they register.
          Move-in date and deposit are optional and can also be added later with Edit.
        </p>
        <input
          name="username"
          placeholder="Username"
          value={newUser.username}
          onChange={handleAddChange}
          style={styles.input}
        />
        <select
          name="role"
          value={newUser.role}
          onChange={handleAddChange}
          style={styles.input}
        >
          {ROLES.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
        {newUser.role === 'TENANT' && (
          <>
            <input
              name="moveInDate"
              type="date"
              title="Move-in date (optional)"
              value={newUser.moveInDate}
              onChange={handleAddChange}
              style={styles.input}
            />
            <input
              name="demandedDeposit"
              type="number"
              min="0"
              step="0.01"
              placeholder="Demanded deposit"
              value={newUser.demandedDeposit}
              onChange={handleAddChange}
              style={{ ...styles.input, width: 140 }}
            />
            <input
              name="depositPaid"
              type="number"
              min="0"
              step="0.01"
              placeholder="Deposit paid so far"
              value={newUser.depositPaid}
              onChange={handleAddChange}
              style={{ ...styles.input, width: 150 }}
            />
            {newUser.depositPaid !== '' && (
              <select name="depositMethod" value={newUser.depositMethod} onChange={handleAddChange} style={{ ...styles.input, width: 130 }} aria-label="How the deposit was paid">
                {DEPOSIT_METHODS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            )}
          </>
        )}
        <button disabled={loading} onClick={handleAddUser} style={styles.btnPrimary}>
          {loading ? 'Please wait…' : 'Add'}
        </button>
        <button onClick={handleExportCSV} style={styles.exportBtn}>Export CSV</button>
        <button onClick={handleExportKeysCSV} style={{ ...styles.exportBtn, background: '#f59e0b' }}>
          Export Registration Keys
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 18 }}>
        <input
          style={{ ...styles.searchBar, marginBottom: 0 }}
          placeholder="Search by username or role"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        {[
          ['ALL', `All (${users.length})`],
          ['REGISTERED', `Registered (${registeredCount})`],
          ['PENDING', `Not registered (${users.length - registeredCount})`],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => { setRegFilter(key); setPage(1); }}
            style={{
              background: regFilter === key ? 'var(--grad-primary)' : 'var(--line)',
              color: regFilter === key ? '#fff' : 'var(--ink-soft)',
              border: 'none', borderRadius: 18, padding: '7px 16px', fontWeight: 'bold', fontSize: 13, cursor: 'pointer',
            }}
          >{label}</button>
        ))}
      </div>

      {loading && <p style={{ textAlign: 'center' }}>Loading...</p>}

      <h4 style={{ color: 'var(--primary)', marginBottom: '16px' }}>Existing Users</h4>
      <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={{ ...styles.th, ...styles.usernameCol }}>Username</th>
            {/* Password column removed to avoid editing hashed passwords */}
            <th style={styles.th}>Full Name</th>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Registration</th>
            <th style={styles.th}>Phone</th>
            <th style={styles.th}>Move-In Date</th>
            <th style={styles.th}>Demanded Deposit</th>
            <th style={styles.th}>Total Deposit</th>
            <th style={{ ...styles.th, ...styles.actionsCol }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pagedUsers.map((u, idx) => (
            <tr
              key={u.id}
              style={{
                background: idx % 2 === 0 ? 'var(--surface-alt)' : '#fff',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <td style={styles.td}>{u.id}</td>

              <td style={{ ...styles.td, ...styles.usernameCol }}>
                {editId === u.id ? (
                  <input
                    name="username"
                    value={editUser.username}
                    onChange={handleEditChange}
                    style={styles.input}
                  />
                ) : (
                  u.username
                )}
              </td>

              {/* Password cell removed */}

              <td style={styles.td}>
                {editId === u.id ? (
                  <input
                    name="fullName"
                    placeholder="Full name"
                    value={editUser.fullName}
                    onChange={handleEditChange}
                    style={{ ...styles.input, width: 130 }}
                  />
                ) : (
                  u.fullName || '—'
                )}
              </td>
              <td style={styles.td}>
                {editId === u.id ? (
                  <select
                    name="role"
                    value={editUser.role}
                    onChange={handleEditChange}
                    style={styles.input}
                  >
                    {ROLES.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                ) : (
                  u.role
                )}
              </td>
              <td style={styles.td}>
                {editId === u.id && editUser.role !== 'ADMIN' ? (
                  <select
                    name="registrationCompleted"
                    value={String(editUser.registrationCompleted)}
                    onChange={handleEditChange}
                    style={styles.input}
                    title="Not registered lets the tenant register again and choose a new password"
                  >
                    <option value="true">Registered</option>
                    <option value="false">Not registered</option>
                  </select>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                      background: u.registrationCompleted ? '#dcfce7' : '#fef3c7',
                      color: u.registrationCompleted ? '#166534' : '#92400e',
                    }}>
                      {u.registrationCompleted ? 'Registered' : 'Not registered'}
                    </span>
                    {!u.registrationCompleted && u.registrationKey && (
                      <input
                        readOnly
                        value={u.registrationKey}
                        onFocus={(e) => e.target.select()}
                        title="Click to select, then copy"
                        style={{ ...styles.input, margin: 0, width: 100, textAlign: 'center', fontSize: 12, fontFamily: 'monospace', cursor: 'text' }}
                      />
                    )}
                  </div>
                )}
              </td>
              <td style={styles.td}>
                {editId === u.id ? (
                  <input
                    type="tel"
                    name="phone"
                    placeholder="10-digit mobile"
                    value={editUser.phone}
                    onChange={handleEditChange}
                    style={{ ...styles.input, width: 110 }}
                  />
                ) : (
                  u.phone || '—'
                )}
              </td>
              <td style={styles.td}>
                {u.role === 'ADMIN' ? (
                  '—'
                ) : editId === u.id ? (
                  <input
                    type="date"
                    name="moveInDate"
                    value={editUser.moveInDate}
                    onChange={handleEditChange}
                    style={styles.input}
                  />
                ) : (
                  u.moveInDate ? formatDate(u.moveInDate) : '—'
                )}
              </td>
              <td style={styles.td}>
                {u.role === 'ADMIN' ? (
                  '—'
                ) : editId === u.id ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="demandedDeposit"
                    placeholder="e.g. 20000"
                    value={editUser.demandedDeposit}
                    onChange={handleEditChange}
                    style={{ ...styles.input, width: 100 }}
                  />
                ) : (
                  u.demandedDeposit != null ? `₹${u.demandedDeposit}` : '—'
                )}
              </td>
              <td style={styles.td}>
                {u.role === 'ADMIN' ? (
                  '—'
                ) : editId === u.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Current: ₹{typeof u.totalAmountDeposited === 'number' ? u.totalAmountDeposited : (u.totalAmountDeposited || 0)}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="manualDepositAmount"
                      placeholder="Add amount"
                      value={editUser.manualDepositAmount}
                      onChange={handleEditChange}
                      style={{ ...styles.input, margin: 0, width: 100 }}
                    />
                    <input
                      type="text"
                      name="manualDepositNotes"
                      placeholder="Note (optional)"
                      value={editUser.manualDepositNotes}
                      onChange={handleEditChange}
                      style={{ ...styles.input, margin: 0, width: 100 }}
                    />
                    {editUser.manualDepositAmount && (
                      <>
                        <select name="manualDepositMethod" value={editUser.manualDepositMethod} onChange={handleEditChange} style={{ ...styles.input, margin: 0, width: 100 }} aria-label="How the deposit was paid">
                          {DEPOSIT_METHODS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                        </select>
                        {editUser.manualDepositMethod !== 'CASH' && (
                          <input
                            type="text"
                            name="manualDepositReference"
                            placeholder="UPI / bank ref no."
                            maxLength={100}
                            value={editUser.manualDepositReference}
                            onChange={handleEditChange}
                            style={{ ...styles.input, margin: 0, width: 100 }}
                          />
                        )}
                        <label style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'left' }}>
                          Received on
                          <input
                            type="date"
                            name="manualDepositReceivedOn"
                            max={new Date().toISOString().slice(0, 10)}
                            value={editUser.manualDepositReceivedOn}
                            onChange={handleEditChange}
                            style={{ ...styles.input, margin: 0, width: 100, display: 'block' }}
                          />
                        </label>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    ₹{typeof u.totalAmountDeposited === 'number' ? u.totalAmountDeposited : (u.totalAmountDeposited || 0)}
                    <br />
                    <button
                      type="button"
                      onClick={() => setDepositHistoryFor(u.username)}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', textDecoration: 'underline', fontSize: 12, cursor: 'pointer' }}
                    >
                      History
                    </button>
                  </>
                )}
              </td>

              <td style={{ ...styles.td, ...styles.actionsCol }}>
                {editId === u.id ? (
                  <>
                    <button
                      onClick={handleUpdateUser}
                      disabled={loading || updatingMoveIn}
                      style={{ ...styles.actionBtn, background: 'var(--primary)', color: '#fff', opacity: (loading || updatingMoveIn) ? 0.7 : 1 }}
                    >
                      {(loading || updatingMoveIn) ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditId(null)}
                      style={{
                        ...styles.actionBtn,
                        background: 'var(--surface-alt)',
                        color: 'var(--primary)',
                        border: '1px solid var(--primary)',
                      }}
                      disabled={loading || updatingMoveIn}
                    >
                      Cancel
                    </button>
                    {updatingMoveIn && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>Updating move-in & deposit…</div>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEdit(u)}
                      style={{ ...styles.actionBtn, background: 'var(--primary-light)', color: '#fff' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(u.id)}
                      style={{ ...styles.actionBtn, background: '#ef4444', color: '#fff' }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Pagination */}
      <div style={styles.pagination}>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => setPage(i + 1)}
            style={{
              ...styles.actionBtn,
              background: page === i + 1 ? 'var(--primary)' : 'var(--surface-alt)',
              color: page === i + 1 ? '#fff' : 'var(--primary)',
              border: '1px solid var(--primary)',
              marginRight: 4,
            }}
          >
            {i + 1}
          </button>
        ))}
      </div>
      {depositHistoryFor && <DepositHistoryModal username={depositHistoryFor} onClose={() => setDepositHistoryFor(null)} />}
    </div>
  );
}

export default AdminUsers;